import * as RadixDialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { AccessScope } from '@fieldvision/contracts';
import { api, apiErrorMessage, canAny, useAuth } from '@fieldvision/web-core';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, KeyRound, LogOut, Menu } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Banner, Button, Dialog, Field, Input } from '../ui/index.js';
import { APP_VERSION } from '../../lib/app-version.js';
import { VERSION_RELOAD_MARK } from '../../lib/build-version.logic.js';
import { notify } from '../../lib/notify.js';
import { useBuildVersion } from '../../lib/use-build-version.js';
import { shouldReload } from '../ErrorBoundary.logic.js';
import { NAV } from './nav.js';

/**
 * The console shell.
 *
 * A fixed sidebar at `lg` and above; below that the same navigation moves into a
 * drawer behind a button in the top bar. There is no separate mobile navigation
 * component — one `NAV` list renders in both places, so a route can never appear
 * in one and be forgotten in the other.
 */
export function Shell({ scope, children }: { scope: AccessScope | undefined; children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const staleBuild = useBuildVersion();

  // A navigation inside the drawer must close it, or the destination renders
  // underneath an open overlay.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  /*
   * A NEW BUILD IS COLLECTED AT THE NEXT NAVIGATION — not the moment it is
   * noticed.
   *
   * Reloading on discovery would be simpler and is wrong: this fires while a
   * manager is looking at the screen, and it would take an open dialog, a
   * half-typed outlet form or an unsaved filter with it. A route change is the
   * one point where nothing is in progress by definition, and it is also when
   * stale code is most likely to matter — the destination is the screen whose
   * queries have not run yet.
   *
   * React Router has already committed the new URL by the time an effect on
   * `pathname` runs, so `reload()` fetches the DESTINATION fresh rather than
   * bouncing back to the page just left.
   *
   * The mark and its cooldown are `ErrorBoundary`'s, reused: a deploy that
   * publishes a `version.json` this bundle can never match would otherwise
   * reload on every click. Its own key, so a chunk reload and a version reload
   * cannot cancel each other out — they are different failures and either may
   * legitimately follow the other.
   */
  const seenPath = useRef(location.pathname);
  useEffect(() => {
    /*
     * A CHANGE, not a value. Without this the effect would fire the instant
     * `staleBuild` turns true — which happens while the manager is reading the
     * page — and reload the screen out from under them, which is the one
     * behaviour this is written to avoid. Only a pathname that DIFFERS from the
     * one last seen counts as a navigation.
     */
    const navigated = seenPath.current !== location.pathname;
    seenPath.current = location.pathname;
    if (!staleBuild || !navigated) return;

    const now = Date.now();
    try {
      if (!shouldReload(window.sessionStorage.getItem(VERSION_RELOAD_MARK), now)) return;
      window.sessionStorage.setItem(VERSION_RELOAD_MARK, String(now));
    } catch {
      // No sessionStorage — a private window, or blocked site data. Without
      // somewhere to record the mark there is no way to tell a first reload from
      // a loop, so the banner stays and the manager reloads when they choose.
      return;
    }
    window.location.reload();
  }, [location.pathname, staleBuild]);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-shell">
      <aside className="hidden border-r border-line bg-white lg:block">
        <div className="sticky top-0 flex h-dvh flex-col">
          <Brand />
          <div className="flex-1 overflow-y-auto px-3 pb-6">
            <NavList scope={scope} />
          </div>
          <VersionFooter />
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <TopBar scope={scope} onOpenDrawer={() => setDrawerOpen(true)} />
        <main className="mx-auto w-full max-w-container flex-1 p-gutter md:p-gutter-md">
          {staleBuild ? (
            <div className="mb-gutter">
              {/*
                NOT `live`. This is standing context — it appears while the
                manager is doing something else and stays until they act on it —
                rather than a response to something they just did, and an
                assertive region would interrupt whatever is being read. Same
                distinction as the locked-period notice on the filter bar.
              */}
              <Banner tone="info">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span>
                    Versi baru FieldVision sudah tersedia. Halaman akan dimuat ulang saat Anda
                    berpindah menu.
                  </span>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="focus-ring shrink-0 rounded font-semibold underline underline-offset-2"
                  >
                    Muat ulang sekarang
                  </button>
                </div>
              </Banner>
            </div>
          ) : null}
          {children}
        </main>
      </div>

      <RadixDialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="fixed inset-0 z-40 bg-[rgba(18,35,59,0.55)] lg:hidden" />
          <RadixDialog.Content className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-white shadow-card lg:hidden">
            <RadixDialog.Title className="sr-only">Navigasi</RadixDialog.Title>
            <Brand />
            <div className="flex-1 overflow-y-auto px-3 pb-6">
              <NavList scope={scope} touch />
            </div>
            <VersionFooter />
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-line px-5">
      {/* The same lockup as the login screen, at sidebar scale (§7.6). */}
      <img src="/logo-mark-96.png" alt="" width={28} height={28} className="h-7 w-7 shrink-0" />
      <span className="font-display text-base font-bold text-navy">FieldVision</span>
    </div>
  );
}

/**
 * The release number, at the foot of the navigation.
 *
 * PINNED TO THE BOTTOM RATHER THAN PLACED AFTER THE LAST NAV GROUP. The sidebar
 * is a `h-dvh` flex column whose nav list is `flex-1 overflow-y-auto`, so this
 * sits below the scroll rather than inside it: on a short window the list
 * scrolls and the version stays visible, which is the whole point of putting it
 * somewhere a manager can be talked to it over the phone.
 *
 * RENDERED IN BOTH THE SIDEBAR AND THE DRAWER, for the reason `NavList` is —
 * they are the same navigation at two breakpoints, and a footer added to one
 * and forgotten in the other means the answer is missing on exactly the smaller
 * screen where support is more likely to be needed.
 *
 * THE RELEASE NUMBER, NOT THE BUILD ID. `__FV_BUILD__` already exists a few
 * lines above and would be the wrong thing to show: it is a commit sha that
 * changes on every deploy, and its job here is the staleness banner, not the
 * reader. See `lib/app-version.ts`.
 */
function VersionFooter() {
  return (
    <div className="shrink-0 border-t border-line px-5 py-3">
      <p className="text-xs text-muted">Versi {APP_VERSION}</p>
    </div>
  );
}

function NavList({ scope, touch = false }: { scope: AccessScope | undefined; touch?: boolean }) {
  const location = useLocation();

  /*
   * The active item is the LONGEST route that prefixes the current path, chosen
   * once across the whole nav rather than tested per item. Testing per item
   * lights up both `/users` and `/users/roles` on the roles screen, because
   * `/users` is a genuine prefix of it — two highlighted items and no way to
   * tell where you are.
   */
  const activePath = NAV.flatMap((group) => group.items)
    .map((item) => item.to)
    .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <nav className="flex flex-col gap-5 pt-4">
      {NAV.map((group) => {
        const visible = group.items.filter((item) => canAny(scope, item.any));
        if (visible.length === 0) return null;

        return (
          <div key={group.label}>
            <p className="eyebrow px-3 pb-2">{group.label}</p>
            <ul className="flex flex-col gap-0.5">
              {visible.map((item) => {
                const active = item.to === activePath;
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`focus-ring flex items-center gap-2.5 rounded-lg px-3 text-sm font-semibold transition-colors duration-150 ${
                        touch ? 'tap-target' : 'h-control'
                      } ${active ? 'bg-tint text-primary-700' : 'text-muted hover:bg-panel hover:text-ink'}`}
                    >
                      {/* Decorative: the text beside it is the accessible name. */}
                      <Icon className="shrink-0" size={18} aria-hidden />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

/**
 * Changing your own password — `POST /auth/password`.
 *
 * It lives in the account menu rather than on a settings page because the
 * console has no profile screen at all: `/settings` is the price-index
 * thresholds, and everything else about a user is administered from `/users` by
 * somebody else. This is the one thing here that is about the caller.
 *
 * SUCCESS SIGNS THE CALLER OUT, and that is the server's doing rather than a
 * choice made here — the endpoint revokes every refresh token the account holds,
 * including the one behind this browser's cookie. Staying on the page would mean
 * an access token with minutes left on it and no way to renew, which fails as a
 * confusing error somewhere else. Better to end it here and say so.
 */
function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const signOut = useAuth((state) => state.signOut);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const change = useMutation({
    mutationFn: () => api.post<void>('/auth/password', { currentPassword, newPassword }),
    meta: { inlineError: true },
    /*
     * The toast OUTLIVES this dialog and the whole console: `Toaster` is mounted
     * beside the router, so it is still on screen after `signOut()` drops the
     * app to the login form. That is the only place this confirmation can be
     * read — and without it, a successful password change and an expired session
     * look identical from the login screen.
     */
    onSuccess: () => {
      notify.success('Kata sandi diubah. Masuk kembali dengan kata sandi baru.');
      signOut();
    },
  });

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmation('');
    change.reset();
  };

  // Purely client-side: the server has no opinion about a confirmation field and
  // should not — it exists so a typo in a password nobody can read back becomes
  // a message here rather than a lockout discovered at the next sign-in.
  const mismatch = confirmation !== '' && confirmation !== newPassword;
  const complete = currentPassword !== '' && newPassword.length >= 6 && confirmation === newPassword;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ubah kata sandi"
      description="Setelah berhasil, Anda akan keluar dari semua perangkat dan perlu masuk kembali."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={change.isPending || !complete}
            onClick={() => change.mutate()}
          >
            {change.isPending ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {change.error ? <Banner tone="error" live>{apiErrorMessage(change.error)}</Banner> : null}

        <Field label="Kata sandi saat ini">
          <Input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </Field>

        <Field
          label="Kata sandi baru"
          hint="Minimal 6 karakter."
          error={newPassword !== '' && newPassword.length < 6 ? 'Terlalu pendek.' : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </Field>

        <Field label="Ulangi kata sandi baru" error={mismatch ? 'Tidak sama.' : undefined}>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function TopBar({ scope, onOpenDrawer }: { scope: AccessScope | undefined; onOpenDrawer: () => void }) {
  const user = useAuth((state) => state.user);
  const signOut = useAuth((state) => state.signOut);
  const [changingPassword, setChangingPassword] = useState(false);

  const logout = async () => {
    // Best effort: the refresh cookie is cleared server-side, but a failed call
    // must not strand the user in a session they asked to end.
    await api.post('/auth/logout').catch(() => undefined);
    // The cache is emptied by the subscription in `main.tsx`, which covers this
    // sign-out and the involuntary one alike. Clearing it here too would be a
    // second mechanism to keep in step with the first.
    signOut();
  };

  return (
    /*
     * OPAQUE, not the frosted `bg-white/90 backdrop-blur` this used to carry.
     *
     * Every list in the console scrolls the PAGE under this bar, and a 10%
     * transparent header shows what passes beneath it: on a long table the rows
     * bleed through the navbar as a moving smear behind the user's own name and
     * the menu button. Blurring it does not fix that — a blur softens what
     * shows through, it does not stop it showing through — and the effect was
     * decorative in the first place, paid for with a compositing layer on a
     * surface that never wanted one.
     *
     * The sticky table headers that park under this bar (`MiniTable` and the
     * guide's tables, at `--sticky-header-top`) slide beneath it by design, on
     * `z-20`/`z-10` against this bar's `z-30`. They could only do that cleanly
     * once there was something solid to slide beneath.
     *
     * The Guide and Legal layouts keep the frosted treatment. They are prose at
     * `max-w-3xl` with their own header, not this one.
     */
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-white px-gutter md:px-gutter-md">
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label="Buka navigasi"
        className="focus-ring tap-target -ml-2 flex items-center justify-center rounded-lg text-muted hover:bg-panel hover:text-ink lg:hidden"
      >
        <Menu size={20} />
      </button>

      <div className="flex-1" />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="focus-ring flex h-control items-center gap-2 rounded-lg px-2 text-sm font-semibold text-ink hover:bg-panel"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">
              {(user?.name ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="hidden sm:inline">{user?.name}</span>
            <ChevronDown size={16} className="text-muted" aria-hidden />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-50 min-w-56 rounded-xl border border-line bg-white p-1 shadow-card"
          >
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-ink">{user?.name}</p>
              <p className="text-sm text-muted">{user?.role}</p>
              <p className="mt-1 text-xs text-muted">
                {scope?.allRegions ? 'Semua Distributor' : `${scope?.regionIds.length ?? 0} Distributor`}
              </p>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-line" />
            <DropdownMenu.Item
              // Radix returns focus to the trigger as it closes, which would
              // steal it back from the dialog opening in the same tick.
              onSelect={(event) => {
                event.preventDefault();
                setChangingPassword(true);
              }}
              className="focus-ring flex h-control cursor-pointer items-center gap-2.5 rounded-lg px-3 text-sm font-semibold text-ink outline-none data-[highlighted]:bg-panel"
            >
              <KeyRound size={16} aria-hidden />
              Ubah kata sandi
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => void logout()}
              className="focus-ring flex h-control cursor-pointer items-center gap-2.5 rounded-lg px-3 text-sm font-semibold text-danger outline-none data-[highlighted]:bg-danger-surface"
            >
              <LogOut size={16} aria-hidden />
              Keluar
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </header>
  );
}
